import React, {FC, useEffect, useMemo, useState} from 'react'
import PlatformCard from './components/PlatformCard'
import ConnectPlatformModal from './components/ConnectPlatformModal'
import {Platform} from 'src/interfaces/Platform.interface'
import {
  connectPlatformAsync,
  fetchConnectedPlatformAsync,
  fetchPlatformsAsync,
} from 'src/redux/actions/platformActions'
import {useAppDispatch, useAppSelector} from 'src/redux/storeHooks'
import {toast} from 'react-toastify'
import useAppToast from 'src/hooks/useAppToast'
import {useNavigate} from 'react-router-dom'
import Loader from 'src/utils/Loader'
import {getInitialSyncStatusAsync} from 'src/redux/actions/authActions'
import {useDebounce} from '../../../hooks/useDebounce'
import {Button, Modal} from 'react-bootstrap'
import ConnectionRoleErrorAlert from 'src/utils/ConnectionRoleErrorAlert'
import {
  ConnectionChoice,
  ConnectionNotice,
  ROLE_CHOICE_CONTENT,
  buildConnectPlatformPayload,
  buildConnectionChannelState,
  getConnectionRoleChoices,
  getDefaultConnectionRoleChoice,
  resolveConnectNature,
  shouldAskConnectionRole,
  validateConnectionRole,
  type ConnectionRoleError,
} from 'src/utils/platformConnectionValidation'

const TYPE_FILTER_TABS: Record<string, string> = {
  TICKET: 'TICKET',
}

const getChannelDisplayLabel = (value?: string | null) => {
  const normalized = `${value ?? ''}`.toUpperCase()
  if (normalized === 'TICKET') return 'HELPDESK'
  if (normalized === 'PRIMARY') return 'Source'
  if (normalized === 'SECONDARY') return 'Destination'
  return value ?? ''
}

type PlatformsProps = {
  nature?: string
  search?: string
  channels?: any
}

const Platforms: FC<PlatformsProps> = ({nature, search}) => {
  const [loading, setLoading] = useState(true)
  const {
    mainStoreData,
    accountingStoreData,
    shippingStoreData,
    erpStoreData,
    omsStoreData,
    ticketStoreData,
    fetchedConnectedPlatformData,
  } = useAppSelector((state) => state.fetchConnectedPlatform)
  const [showConnectPlatformModal, setShowConnectPlatformModal] = useState<boolean>(false)
  const [showConnectionRoleModal, setShowConnectionRoleModal] = useState<boolean>(false)
  const [selectedPlatform, setSelectedPlatform] = useState<Platform | null>(null)
  const [selectedConnectNature, setSelectedConnectNature] = useState<string | undefined>(nature)
  const [connectionRoleChoice, setConnectionRoleChoice] = useState<ConnectionChoice>('PRIMARY')
  const [connectionNotice, setConnectionNotice] = useState<ConnectionNotice | null>(null)
  const [connectionRoleError, setConnectionRoleError] = useState<ConnectionRoleError | null>(null)
  const [ecommercePlatform, setEcommercePlatform] = useState<any>([])
  const {successToast, warningToast, errorToast} = useAppToast()
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const debouncedSearch = useDebounce(search, 500)
  const hasSearch = Boolean(debouncedSearch?.trim())

  useEffect(() => {
    if (!showConnectPlatformModal) {
      setSelectedConnectNature(nature)
    }
  }, [nature, showConnectPlatformModal])

  const channelState = useMemo(
    () =>
      buildConnectionChannelState({
        mainStoreData,
        accountingStoreData,
        shippingStoreData,
        erpStoreData,
        omsStoreData,
        ticketStoreData,
        fetchedConnectedPlatformData,
      }),
    [
      mainStoreData,
      accountingStoreData,
      shippingStoreData,
      erpStoreData,
      omsStoreData,
      ticketStoreData,
      fetchedConnectedPlatformData,
    ]
  )

  const hasSourceChannel =
    channelState.hasPrimaryChannel ||
    channelState.hasAccountingChannel ||
    channelState.hasShippingChannel ||
    channelState.hasErpChannel ||
    channelState.hasOmsChannel ||
    channelState.hasTicketChannel

  const showConnectionRoleMessage = (
    platform: Platform,
    connectNature: string,
    showInlineError = false
  ) => {
    const validation = validateConnectionRole(platform, connectNature, channelState, {
      showInlineError,
    })

    if (!validation.ok) {
      if (showInlineError) {
        setConnectionRoleError(validation.errorContent ?? validation.error ?? null)
      } else {
        errorToast(validation.error ?? 'This platform cannot be connected in the selected role.')
      }
      return false
    }

    setConnectionNotice(validation.notice ?? null)
    return true
  }

  const onClickConnect = (platform: Platform) => {
    setConnectionNotice(null)
    setConnectionRoleError(null)

    if (shouldAskConnectionRole(platform)) {
      const defaultRole = getDefaultConnectionRoleChoice(platform, channelState)
      setSelectedPlatform(platform)
      setConnectionRoleChoice(defaultRole)
      setSelectedConnectNature(defaultRole)
      setShowConnectionRoleModal(true)
      return
    }

    const connectNature = resolveConnectNature(platform, channelState, {
      activeNature: nature,
      skipTypeFilterTabs: Object.keys(TYPE_FILTER_TABS),
    })
    if (!showConnectionRoleMessage(platform, connectNature)) {
      return
    }
    setSelectedConnectNature(connectNature)
    setSelectedPlatform(platform)
    setShowConnectPlatformModal(true)
  }

  const handleConnectionRoleSelection = (connectNature: ConnectionChoice) => {
    setConnectionRoleError(null)
    setConnectionNotice(null)
    if (!selectedPlatform || !showConnectionRoleMessage(selectedPlatform, connectNature, true)) {
      return
    }

    setSelectedConnectNature(connectNature)
    setShowConnectionRoleModal(false)
    setShowConnectPlatformModal(true)
  }

  const handleConnectionRoleProceed = () => {
    handleConnectionRoleSelection(connectionRoleChoice)
  }

  const handleCloseModal = () => {
    setShowConnectPlatformModal(false)
    setShowConnectionRoleModal(false)
    setSelectedPlatform(null)
    setSelectedConnectNature(nature)
    setConnectionNotice(null)
    setConnectionRoleError(null)
  }

  useEffect(() => {
    setLoading(true)
    if (search !== debouncedSearch || !nature) return

    const normalizedNature = (nature ?? '').toUpperCase()
    const platformTypeFilter = TYPE_FILTER_TABS[normalizedNature]
    const needsFullCatalog =
      Boolean(platformTypeFilter) || (normalizedNature === 'ALL' && hasSearch)

    const query: {nature?: string; search?: string} = {}
    if (!needsFullCatalog) {
      if (nature) query.nature = nature
      if (debouncedSearch) query.search = debouncedSearch
    }

    dispatch(fetchPlatformsAsync(Object.keys(query).length > 0 ? query : undefined)).then(
      (response) => {
        let platformList = response?.payload?.data
        if (platformTypeFilter && Array.isArray(platformList)) {
          platformList = platformList.filter(
            (platform) => (platform?.type ?? '').toUpperCase() === platformTypeFilter
          )
        } else if (normalizedNature === 'ALL' && !hasSearch && Array.isArray(platformList)) {
          platformList = platformList.filter(
            (platform) => (platform?.type ?? '').toUpperCase() !== 'TICKET'
          )
        }
        if (hasSearch && Array.isArray(platformList)) {
          const term = (debouncedSearch ?? '').trim().toLowerCase()
          platformList = platformList.filter(
            (platform) =>
              `${platform?.name ?? ''}`.toLowerCase().includes(term) ||
              `${platform?.display_name ?? ''}`.toLowerCase().includes(term)
          )
        }
        setEcommercePlatform(platformList ?? [])
        setLoading(false)
      }
    )
  }, [nature, debouncedSearch, dispatch, search, hasSearch])

  const handleDataReceived = (
    dataCollector: any,
    friendlyName: string,
    connection_type?: string,
    extras?: Record<string, any>
  ) => {
    if (dataCollector['friendlyName'] == '') {
      warningToast('Channel name is required')
      return
    }

    if (!selectedPlatform) return

    const connectNature =
      selectedConnectNature ??
      resolveConnectNature(selectedPlatform, channelState, {
        activeNature: nature,
        skipTypeFilterTabs: Object.keys(TYPE_FILTER_TABS),
      })

    const payload = buildConnectPlatformPayload(
      selectedPlatform,
      dataCollector,
      friendlyName,
      connectNature,
      connection_type,
      {extras, channelState}
    )

    dispatch(connectPlatformAsync(payload))
      .then((response) => {
        dispatch(fetchConnectedPlatformAsync())
        if (response.meta.requestStatus === 'fulfilled') {
          const {
            id,
            name,
            platform_info,
            isMainStore: responseMainStore,
            isAccountingStore: responseAccountingStore,
            isShippingStore: responseShippingStore,
            isErpStore: responseErpStore,
            isOmsStore: responseOmsStore,
            isTicketStore: responseTicketStore,
          } = response.payload.data
          localStorage.setItem(`platformFirstConnect_${name}_${id.toString()}`, 'true')

          const shouldPassPlatformId = !responseMainStore
          const initialSyncPayload = shouldPassPlatformId ? {platform_id: id} : {}
          dispatch(getInitialSyncStatusAsync(initialSyncPayload))

          const statusCode = response.payload.status_code
          const platformName = payload.name
          const connectedAsSource =
            responseMainStore ||
            responseAccountingStore ||
            responseShippingStore ||
            responseErpStore ||
            responseOmsStore ||
            responseTicketStore
          const shouldOpenSettings = connectedAsSource || hasSourceChannel

          if (response?.payload?.status_code == 201) {
            successToast(`Platform ${platformName} connected successfully!`)
            setShowConnectPlatformModal(false)
            setSelectedPlatform(null)
            setConnectionNotice(null)
            setConnectionRoleError(null)
            if (shouldOpenSettings) {
              navigate(`/channel-settings/${platform_info?.id}/${id}`)
            } else {
              navigate('/platforms?nature=primary')
            }
            return
          }

          return errorToast(`Failed to connect platform ${platformName}. Status code: ${statusCode}`)
        }

        console.error('Connection failed:', response.payload)
        return errorToast('Failed to connect channel. ' + response?.payload?.data?.massage)
      })
      .catch((error) => {
        console.error('Error connecting platform:', error)
        toast.error('An error occurred while connecting the platform. Please try again.')
      })
  }

  if (loading) return <Loader />

  return (
    <>
      <div className=''>
        <h4 className='mb-2  mt-4 mb-md-0' style={{color: 'slategray'}}>
          Connect your {getChannelDisplayLabel(nature)} Channel
        </h4>
        <div className='fw-medium fs-6 ms-1' style={{color: 'lightslategray'}}>
          {nature === 'PRIMARY'
            ? 'Source Channel is your core sales platform where all orders are processed and inventory is managed'
            : nature === 'TICKET'
            ? 'Connect helpdesk platforms to sync tickets and customer support data'
            : 'Connect marketplaces, online stores, and other platforms where you sell your products'}
        </div>
      </div>
      <div className='py-4'>
        <div className='row g-3 gx-2 g-xl-8 my-2'>
          {ecommercePlatform?.length > 0 ? (
            ecommercePlatform.map((item: any) => (
              <React.Fragment key={item.id}>
                <PlatformCard data={item} onClickConnect={() => onClickConnect(item)} />
              </React.Fragment>
            ))
          ) : (
            <div className='p-4 bg-light-primary'>
              <h5>
                {hasSearch
                  ? "We couldn't find any platform matching your search"
                  : 'No Available Platforms found'}
              </h5>
            </div>
          )}
        </div>
      </div>
      {showConnectPlatformModal ? (
        <ConnectPlatformModal
          isVisible={showConnectPlatformModal}
          closeModal={handleCloseModal}
          data={selectedPlatform}
          onDataReceived={handleDataReceived}
          nature={selectedConnectNature}
          connectionNotice={connectionNotice}
        />
      ) : null}
      <Modal
        show={showConnectionRoleModal}
        onHide={handleCloseModal}
        centered
        backdrop='static'
        size='lg'
      >
        <Modal.Header closeButton>
          <Modal.Title>Select connection type</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className='fw-semibold mb-2'>{selectedPlatform?.display_name}</div>
          <div className='text-muted mb-5'>
            This channel can be connected in multiple ways. Choose how you want to connect it.
          </div>
          {connectionRoleError ? <ConnectionRoleErrorAlert error={connectionRoleError} /> : null}
          <div className='row g-4'>
            {(selectedPlatform ? getConnectionRoleChoices(selectedPlatform) : []).map((roleChoice) => {
              const content = ROLE_CHOICE_CONTENT[roleChoice]
              const inputId = `available${roleChoice.toLowerCase()}Connection`
              const isSelected = connectionRoleChoice === roleChoice

              return (
                <div className='col-sm-6 col-12' key={roleChoice}>
                  <label
                    htmlFor={inputId}
                    className={`border p-4 rounded shadow-sm h-100 d-flex align-items-start w-100 cursor-pointer bg-hover-light-primary ${
                      isSelected ? 'border-primary bg-light-primary' : ''
                    }`}
                    style={{minHeight: '120px'}}
                  >
                    <input
                      id={inputId}
                      value={roleChoice}
                      checked={isSelected}
                      type='radio'
                      className='form-check-input mt-1 flex-shrink-0'
                      style={{width: '18px', height: '18px'}}
                      onChange={(ev) => {
                        const selectedRole = ev.target.value as ConnectionChoice
                        setConnectionRoleChoice(selectedRole)
                        setSelectedConnectNature(selectedRole)
                      }}
                    />
                    <div className='ms-4 flex-grow-1'>
                      <div
                        className={`fs-5 ${
                          isSelected ? 'text-primary fw-bold' : 'text-dark fw-semibold'
                        }`}
                      >
                        {content.label}
                      </div>
                      <div className='text-muted fs-7 mt-1'>{content.description}</div>
                    </div>
                  </label>
                </div>
              )
            })}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant='light' onClick={handleCloseModal}>
            Cancel
          </Button>
          <Button variant='primary' onClick={handleConnectionRoleProceed}>
            Continue
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  )
}

export default Platforms
